import type { JSX } from 'react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { ME_KEY, isMeKey } from '../auth/use-me';
import type { Me } from '../auth/use-me';
import { initials, roleLabel } from '@/lib/format';
import { useEscape } from './use-escape';
import { navigate, replaceRoute } from './use-route';
import { HOME } from './routes';
import { WorkspaceMenu } from '../workspaces/workspace-menu';

/** The signed-in person's initials; open, it shows who they are and signs them out. */
export function UserMenu({ me }: { me: Me }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState<'one' | 'all' | undefined>(undefined);
  const queryClient = useQueryClient();
  useEscape(() => setOpen(false));

  /** `all` revokes every session this person holds, on every device. */
  const signOut = async (scope: 'one' | 'all'): Promise<void> => {
    setLeaving(scope);
    const call = scope === 'all' ? api.auth.logoutAll : api.auth.logout;
    // Whatever the API said, nothing fetched under this session is shown again.
    await call({ body: {} }).catch(() => undefined);
    // `me` is set, not removed: the app is subscribed to it, and a removed
    // query would leave it showing the panel. Null is what brings up sign-in.
    queryClient.setQueryData(ME_KEY, null);
    queryClient.removeQueries({ predicate: (query) => !isMeKey(query.queryKey) });
    replaceRoute(HOME);
  };

  return (
    <div className="relative">
      <button
        type="button"
        title={me.name}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="bg-surf hover:bg-surf2 grid size-9 place-items-center rounded-[10px] border text-[12.5px] font-semibold transition-colors"
      >
        {initials(me.name)}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} className="fixed inset-0 z-[25]" />
          <div
            role="menu"
            className="bg-surf shadow-panel absolute top-[calc(100%+6px)] right-0 z-[26] flex w-60 animate-[rise_.18s_ease_both] flex-col gap-0.5 rounded-[10px] border p-[5px]"
          >
            <div className="flex flex-col gap-1 px-2.5 pt-2 pb-2.5">
              <div className="truncate text-sm font-semibold">{me.name}</div>
              <div className="text-mute truncate text-[12.5px]">{me.email}</div>
              <span className="bg-surf2 mt-1 w-fit rounded-full border px-[9px] py-[3px] text-xs">
                {roleLabel(me.role)}
              </span>
            </div>
            <div className="bg-line h-px" />
            <WorkspaceMenu me={me} onDone={() => setOpen(false)} />
            <div className="bg-line h-px" />
            {/* The phone's tab bar has no Users tab, so it lives here on a phone. */}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate({ page: 'users' });
              }}
              className="hover:bg-surf2 rounded-[7px] border-0 bg-transparent px-2.5 py-[9px] text-left text-[13.5px] tab:hidden"
            >
              Users
            </button>
            <button
              type="button"
              role="menuitem"
              disabled={leaving !== undefined}
              onClick={() => void signOut('one')}
              className="hover:bg-surf2 rounded-[7px] border-0 bg-transparent px-2.5 py-[9px] text-left text-[13.5px]"
            >
              {leaving === 'one' ? 'Signing out…' : 'Sign out'}
            </button>
            <button
              type="button"
              role="menuitem"
              title="Ends every session, on every device"
              disabled={leaving !== undefined}
              onClick={() => void signOut('all')}
              className="hover:bg-surf2 rounded-[7px] border-0 bg-transparent px-2.5 py-[9px] text-left text-[13.5px]"
            >
              {leaving === 'all' ? 'Signing out…' : 'Sign out everywhere'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
